/**
 * Project Detail → Files tab.
 *
 * 1:1 with the design folder's `project-details.html#ptab-files`
 * (lines 1567-1595) and 100% schema parity with mobile
 * `src/features/projects/tabs/DesignTab.tsx`.
 *
 * Layout:
 *   Header: "DOCUMENT REPOSITORY · N" eyebrow + "+ UPLOAD" CTA
 *           (admin / designer only)
 *   Chip row (mobile parity): All · 2D · 3D · Layout · MOM ·
 *           Agreement · Other — each with live count.
 *   Grid (.file-grid): auto-fill `minmax(200px, 1fr)` of file
 *           cards. Each card:
 *             • 56×56 thumbnail (image preview) OR file-type icon
 *               (PDF red, image blue, CAD green, etc.)
 *             • Title (2-line clamp)
 *             • Size · relative time
 *             • Tiny category pill top-right
 *
 * Click a card → opens `FileFormSlideOver` in edit mode (which
 * also has a "Download" link to the original file).
 * "+ UPLOAD" → opens it in create mode.
 *
 * Capability gate: only `canEdit` users (org owner / superAdmin /
 * admin / manager / siteEngineer / designer) see the Upload CTA
 * and can edit / delete. Clients can read but not write — the
 * Firestore rule enforces this anyway.
 */
import { useMemo, useState } from 'react';

import { FileFormSlideOver } from '@/components/FileFormSlideOver';
import {
  FILE_CATEGORIES,
  fileTypeIcon,
  formatBytes,
  formatDesignRelTime,
  getCategoryLabel,
  type Design,
  type FileCategory,
} from '@/lib/designs';
import { useDesigns } from '@/lib/useDesigns';
import { useFullOrganization } from '@/lib/useFullOrganization';
import type { ProjectListItem } from '@/lib/useProjects';

const ALL_KEY = 'all' as const;
type FilterKey = typeof ALL_KEY | FileCategory;

export function FilesTab({ project }: { project: ProjectListItem }) {
  const { org } = useFullOrganization();
  const orgId = project.orgId ?? org?.id ?? null;

  const { data: files, loading, canEdit } = useDesigns(
    project.id,
    orgId ?? undefined,
  );

  const [filter, setFilter] = useState<FilterKey>(ALL_KEY);
  const [formMode, setFormMode] = useState<
    { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; item: Design }
  >({ kind: 'closed' });

  // Live counts per category (drives the chip labels).
  const counts = useMemo(() => {
    const m = new Map<FileCategory, number>();
    for (const c of FILE_CATEGORIES) m.set(c.key, 0);
    for (const f of files) {
      const k = (f.category ?? 'other') as FileCategory;
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [files]);

  const visible = useMemo(() => {
    if (filter === ALL_KEY) return files;
    return files.filter((f) => (f.category ?? 'other') === filter);
  }, [files, filter]);

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--text-secondary)',
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          Document Repository · {files.length}
        </h3>
        {canEdit ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setFormMode({ kind: 'create' })}
            disabled={!orgId}
            style={{ padding: '6px 16px', borderRadius: 20, fontSize: 12 }}
          >
            <i className="ph-bold ph-upload-simple" /> UPLOAD
          </button>
        ) : null}
      </div>

      {/* Category filter chips */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          marginBottom: 18,
        }}
      >
        <FilterChip
          label={`All · ${files.length}`}
          active={filter === ALL_KEY}
          onClick={() => setFilter(ALL_KEY)}
        />
        {FILE_CATEGORIES.map((c) => (
          <FilterChip
            key={c.key}
            label={`${c.label} · ${counts.get(c.key) ?? 0}`}
            active={filter === c.key}
            onClick={() => setFilter(c.key)}
          />
        ))}
      </div>

      {/* Grid / loading / empty */}
      {loading && files.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          Loading files…
        </div>
      ) : visible.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>
            <i className="ph ph-file" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
            {filter === ALL_KEY
              ? 'No files uploaded'
              : `No files in ${getCategoryLabel(filter as FileCategory)}`}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              maxWidth: 420,
              margin: '0 auto 16px',
            }}
          >
            {canEdit
              ? 'Add 2D layouts, 3D renders, PDFs, MOMs, agreements — anything. The studio team can re-upload to replace a file in place.'
              : 'The studio will publish project documents here as the work progresses.'}
          </div>
          {canEdit ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setFormMode({ kind: 'create' })}
              disabled={!orgId}
            >
              <i className="ph-bold ph-upload-simple" /> Upload your first file
            </button>
          ) : null}
        </div>
      ) : (
        <div className="file-grid">
          {visible.map((f) => (
            <FileCard
              key={f.id}
              file={f}
              onClick={
                canEdit
                  ? () => setFormMode({ kind: 'edit', item: f })
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {/* Form */}
      <FileFormSlideOver
        open={formMode.kind !== 'closed'}
        onClose={() => setFormMode({ kind: 'closed' })}
        orgId={orgId}
        projectId={project.id}
        item={formMode.kind === 'edit' ? formMode.item : null}
      />
    </div>
  );
}

// ───── Card ─────

function FileCard({
  file,
  onClick,
}: {
  file: Design;
  onClick?: () => void;
}) {
  const ago = formatDesignRelTime(file.updatedAt?.toDate() ?? null);
  const isImage =
    file.thumbnailUrl != null && file.thumbnailUrl.length > 0;
  const icon = fileTypeIcon(file.fileContentType, file.fileName);
  const catLabel = getCategoryLabel(file.category);
  const clickable = !!onClick;

  return (
    <div
      className="file-card"
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!();
              }
            }
          : undefined
      }
      title={clickable ? `Open ${file.title}` : file.title}
      style={{ cursor: clickable ? 'pointer' : 'default' }}
    >
      <span className="file-cat-pill">{catLabel}</span>

      {isImage ? (
        <div
          className="file-thumb-wrap"
          style={{ backgroundImage: `url(${file.thumbnailUrl})` }}
        />
      ) : (
        <div className="file-icon-wrap" style={{ color: icon.color }}>
          <i className={icon.icon} />
        </div>
      )}

      <div className="file-title">{file.title}</div>
      <div className="file-meta">
        {formatBytes(file.fileSizeBytes)} · {ago.toUpperCase()}
      </div>
    </div>
  );
}

// ───── Filter chip ─────

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '5px 12px',
        border: active
          ? '1.5px solid var(--brand)'
          : '1px solid var(--border)',
        background: active ? 'var(--brand-bg)' : '#fff',
        color: active ? 'var(--brand)' : 'var(--text-secondary)',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: active ? 700 : 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
        letterSpacing: 0.4,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </button>
  );
}
